/**
 * 기술지원확약서 탭 — 목록 + 작성/수정/삭제 + PDF 다운로드.
 *
 * 목록 컬럼 (본문 정보): 수신(=수요기관) / 사업명 / 참조 / 제품명
 * 운영 컬럼: 문서번호, 발급일자, 작성자, 액션(워드/수정/삭제) — v2.112.0: 행 클릭 = PDF 미리보기
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth-context';
import {
  createTechSupport,
  deleteTechSupport,
  getTechSupport,
  linkTechSupportToSales,
  listTechSupport,
  techSupportPdfUrl,
  techSupportDocxUrl,
  updateTechSupport,
  type TechSupportDetail,
  type TechSupportInput,
  type TechSupportRow,
} from '../../lib/tech-support-api';
import { TechSupportFormModal } from './TechSupportFormModal';
import { SalesPickerModal } from './SalesPickerModal';
import { NasSaveButton } from '../../components/NasSaveButton';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { mailSubject } from '../../lib/mail-subject';
import { LoadingLines } from '../../components/ui/Skeleton';

export function TechSupportTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<TechSupportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formInitial, setFormInitial] = useState<TechSupportDetail | null>(null);

  // 영업건 연동 (v2.94.0) — NAS 보관 폴더가 영업건 기준이라 필요하다
  const [linkTarget, setLinkTarget] = useState<TechSupportRow | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listTechSupport());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const canModify = (row: TechSupportRow): boolean => {
    if (!user) return false;
    // 2026-05-29: admin/mgmt/sales 모두 타인 작성건 수정·삭제 가능 (backend 와 동일)
    if (user.role === 'admin' || user.role === 'mgmt' || user.role === 'sales') return true;
    if (user.userId === row.createdBy) return true;
    return false;
  };

  const filtered = rows.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      r.docNo.toLowerCase().includes(q) ||
      r.recipient.toLowerCase().includes(q) ||
      r.businessName.toLowerCase().includes(q) ||
      r.ccRef.toLowerCase().includes(q) ||
      r.productName.toLowerCase().includes(q)
    );
  });

  function handleCreateClick() {
    setFormMode('create');
    setFormInitial(null);
    setFormOpen(true);
  }

  async function handleEditClick(row: TechSupportRow) {
    try {
      const detail = await getTechSupport(row.id);
      setFormMode('edit');
      setFormInitial(detail);
      setFormOpen(true);
    } catch (e) {
      alert('조회 실패: ' + (e as Error).message);
    }
  }

  async function handleDeleteClick(row: TechSupportRow) {
    if (!confirm(`기술지원확약서 [${row.docNo}] 를 삭제하시겠습니까?\n( ${row.recipient || '수요기관 없음'} )`)) {
      return;
    }
    try {
      await deleteTechSupport(row.id);
      await reload();
    } catch (e) {
      alert('삭제 실패: ' + (e as Error).message);
    }
  }

  /** 미리보기 — **행 클릭**으로 연다 (v2.112.0. 같은 일을 하던 📄 버튼은 뺐다).
   *  ✉ 메일은 PDF·워드 두 형식이라 미리보기 창에서 고른다. */
  function openPreview(r: TechSupportRow): void {
    openServerPdfPreview({
      pdfUrl: techSupportPdfUrl(r.id),
      filename: `기술지원확약서_${r.docNo}.pdf`,
      mailDoc: {
        docRefs: [
          { docType: 'tech-support', docId: r.id, label: `기술지원확약서 ${r.docNo} (PDF)` },
          { docType: 'tech-support-docx', docId: r.id, label: `기술지원확약서 ${r.docNo} (워드)` },
        ],
        subject: mailSubject('기술지원확약서 송부', r.docNo),
      },
    });
  }

  async function handleFormSubmit(data: TechSupportInput) {
    if (formMode === 'create') {
      const res = await createTechSupport(data);
      setFormOpen(false);
      await reload();
      // 작성 직후 PDF 미리보기 팝업
      openServerPdfPreview({ pdfUrl: techSupportPdfUrl(res.id), filename: '기술지원확약서.pdf' });
    } else if (formMode === 'edit' && formInitial) {
      await updateTechSupport(formInitial.id, data);
      setFormOpen(false);
      await reload();
    }
  }

  async function handleLinkPick(salesId: string) {
    if (!linkTarget) return;
    await linkTechSupportToSales(linkTarget.id, salesId);
    setLinkTarget(null);
    await reload();
  }

  async function handleLinkUnlink() {
    if (!linkTarget) return;
    await linkTechSupportToSales(linkTarget.id, null);
    setLinkTarget(null);
    await reload();
  }

  const canCreate = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5">
      {/* 헤더 */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="ui-section-title mr-auto">
          기술지원확약서 목록 ({filtered.length}건)
        </h2>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="문서번호/수요기관/사업명/참조/제품명 검색"
          className="ui-input w-72"
        />
        {canCreate && (
          <button
            type="button"
            onClick={handleCreateClick}
            className="btn btn-primary"
          >
            + 확약서 작성
          </button>
        )}
      </div>

      {err && (
        <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
          {err}
        </div>
      )}

      {/* 목록 */}
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <Th>문서번호</Th>
              <Th>발급일자</Th>
              <Th>수신</Th>
              <Th>사업명</Th>
              <Th>참조</Th>
              <Th>제품명</Th>
              <Th>작성자</Th>
              <Th align="center">워드</Th>
              <Th align="center">작업</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-center py-6 text-slate-400"><LoadingLines /></td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-6 text-slate-400">
                  기술지원확약서 없음
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const firstProduct = r.productName.split('\n')[0] ?? '';
                const hasMore = r.productName.split('\n').length > 1;
                return (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openPreview(r)}
                    title="클릭하면 미리보기"
                  >
                    <Td className="font-mono text-xs">{r.docNo}</Td>
                    <Td>{r.issueDate}</Td>
                    <Td className="max-w-[20ch] truncate" title={r.recipient}>{r.recipient}</Td>
                    {/* 사업명 + 영업건 연동 상태 — 칸을 새로 만들지 않고 같은 칸 아래줄에 둔다
                        (칸을 늘리면 좁은 화면에서 표가 밀린다) */}
                    <Td className="max-w-[24ch]">
                      <div className="truncate" title={r.businessName}>{r.businessName}</div>
                      {r.linkedSalesId ? (
                        <div
                          className="font-mono text-xs text-blue-700 truncate"
                          title={
                            r.linkedProjectName
                              ? `연동된 영업건: ${r.linkedSalesId} · ${r.linkedProjectName}`
                              : `연동된 영업건 ${r.linkedSalesId} 을(를) 찾을 수 없습니다 (삭제된 듯합니다)`
                          }
                        >
                          🔗 {r.linkedSalesId}
                          {!r.linkedProjectName && <span className="text-rose-600"> (없음)</span>}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500">영업건 미연동</div>
                      )}
                    </Td>
                    <Td className="max-w-[16ch] truncate" title={r.ccRef}>{r.ccRef}</Td>
                    <Td className="max-w-[24ch] truncate" title={r.productName}>
                      {firstProduct}
                      {hasMore && <span className="text-slate-500"> 외</span>}
                    </Td>
                    <Td className="text-xs text-slate-500">{r.createdBy}</Td>
                    {/* v2.112.0 — [📄 미리보기] 버튼은 뺐다(행 클릭과 같은 일이라 중복).
                        📝 워드 다운로드는 다른 일이라 남긴다. */}
                    <Td align="center" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={techSupportDocxUrl(r.id)}
                        className="inline-block px-2 py-0.5 text-xs bg-sky-50 hover:bg-sky-100 text-sky-700 rounded border border-sky-200"
                        title="워드 양식 다운로드 (로고·인감 포함)"
                      >
                        📝
                      </a>
                    </Td>
                    <Td align="center" onClick={(e) => e.stopPropagation()}>
                      {canModify(r) ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setLinkTarget(r)}
                            className={`btn btn-sm mr-1 ${r.linkedSalesId ? 'btn-outline' : 'btn-secondary'}`}
                            title={
                              r.linkedSalesId
                                ? `영업건 연동됨 (${r.linkedSalesId}) — 눌러서 바꾸거나 풀기`
                                : '영업건 연동 — NAS 보관 폴더가 영업건 기준이라 연동해야 저장할 수 있습니다'
                            }
                          >
                            🔗
                          </button>
                          {/* NAS 저장 (v2.95.0) — 영업건이 연동돼야 저장 폴더가 정해진다.
                              영업건이 지워졌으면 번호는 남고 사업명이 비므로 그때도 저장 불가. */}
                          <span className="mr-1 inline-block">
                            <NasSaveButton
                              docType="tech-support"
                              docId={r.id}
                              canSave={!!r.linkedSalesId && !!r.linkedProjectName}
                              blockedReason={
                                r.linkedSalesId
                                  ? `연동된 영업건(${r.linkedSalesId})을 찾을 수 없어 저장할 폴더를 정할 수 없습니다.`
                                  : '연동된 영업건이 없어 저장할 폴더를 정할 수 없습니다.'
                              }
                              onRequestLink={() => setLinkTarget(r)}
                            />
                          </span>
                          <button
                            type="button"
                            onClick={() => handleEditClick(r)}
                            className="btn btn-warning btn-sm mr-1"
                            title="수정"
                          >
                            ✏
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(r)}
                            className="btn btn-danger btn-sm"
                            title="삭제"
                          >
                            🗑
                          </button>
                        </>
                      ) : (
                        <span className="text-[11px] text-slate-500">조회만</span>
                      )}
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <TechSupportFormModal
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      {/* 영업건 연동 창.
          ⚠ 사업명을 검색어로 미리 채우지 않는다 — 문서의 사업명과 영업건의 사업명이 대개 다르게
            적혀 있어 열자마자 「0건」이 뜨고 고장으로 보인다(2026-08-26 사용자 신고).
            대신 힌트로 보여 주고 [이 이름으로 찾기] 를 누를 수 있게 한다. */}
      <SalesPickerModal
        open={linkTarget !== null}
        docTitle={linkTarget?.businessName ?? ''}
        currentSalesId={linkTarget?.linkedSalesId ?? null}
        onPick={handleLinkPick}
        onUnlink={linkTarget?.linkedSalesId ? handleLinkUnlink : undefined}
        onClose={() => setLinkTarget(null)}
      />
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`px-2 py-1.5 text-xs font-medium text-slate-600 whitespace-nowrap ${cls}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  className,
  title,
  onClick,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  title?: string;
  /** 행 클릭(미리보기)이 번지지 않게 막을 때 쓴다 — 버튼이 든 칸 (v2.112.0) */
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <td
      className={`px-2 py-1.5 text-sm text-slate-700 align-middle ${cls} ${className ?? ''}`}
      title={title}
      onClick={onClick}
    >
      {children}
    </td>
  );
}
